/**
 * tabWorkspace.ts — the PER-TAB active-organization pin (owner report
 * 2026-09-11: two people signed into one account on different devices; one
 * switched organizations and the other's portal went strange — the header
 * named the old org, the dropdown check sat on the new one, and the row he
 * needed to click was inert).
 *
 * ROOT CAUSE this module closes: `users/{uid}/account_membership/main` holds
 * ONE accountId for the whole account, while the client is built on the
 * invariant that the active workspace cannot move underneath a loaded page
 * (activeWorkspace.ts: "workspace switches do a hard navigation … so this
 * value is stable for the lifetime of a loaded page"). A switch IS a hard
 * reload — but only on the tab that clicked it. Every other tab kept serving
 * the old org from its module caches while its realtime subscribers (wallet,
 * VM banner, trial) followed the pointer instantly and RBAC followed it 60s
 * later: one page, three organizations, no reload.
 *
 * THE LAW THIS RESTORES is not new. PLAN-multi-org-customer-journey-20260816
 * D4 already says it: "Per-tab correctness does not depend on the global
 * pointer … the global pointer is only a default, never an authority", and
 * E9 ("Two tabs, two orgs … the global pointer is a default only"). P3's gate
 * list required a "sticky-context cross-device pin" that was never built.
 * This is that pin.
 *
 * THE LADDER (resolveTabWorkspace) — first hit wins:
 *   1. TAB PIN      sessionStorage, this tab's own choice.
 *   2. THIS DEVICE  localStorage confirmedWorkspaceCache, when FRESH — seeds
 *                   a newly-opened tab so it lands where this device was.
 *   3. ACCOUNT      the server back-pointer — seeds a new device, or one whose
 *                   device record has gone stale.
 * A lone user's three tiers agree, so they always return to where they left
 * off; two tabs in different orgs simply hold different pins and never
 * disturb each other. The split needs no presence system — and there is none
 * in this codebase to build on.
 *
 * Leaf module: no firebase imports, no React (the confirmedWorkspaceCache.ts /
 * backPointer.ts idiom) so the whole decision unit-tests in isolation.
 */

import { readConfirmedWorkspace } from './confirmedWorkspaceCache';

const TAB_PIN_SESSION_KEY = 'prest0_tab_workspace_v1';

/**
 * How long this DEVICE's last workspace may seed a newly-opened tab (owner
 * decision 2026-09-11: "its own, if recently used"). Inside the window a new
 * tab reopens where this device was; past it the account's most recent org
 * wins, so an old laptop does not reopen somewhere its owner left weeks ago.
 * Distinct from confirmedWorkspaceCache's 30-day hygiene bound, which exists
 * to keep the transient-failure hold working and must not be shortened.
 */
export const DEVICE_SEED_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface TabWorkspacePin {
  /** Identity of the tab holding this pin — fresh per boot, never reused across live tabs. */
  tabId: string;
  /**
   * Whose pin this is. A tab outlives a sign-out, so without this a second
   * person signing in on the same tab would inherit the first one's
   * organization. Bound to the record rather than cleared on sign-out
   * because a clear that fails to fire is invisible; a uid that does not
   * match is simply not this user's pin.
   */
  uid: string;
  accountId: string;
  atMs: number;
}

export type TabWorkspaceSource = 'tab-pin' | 'device' | 'account' | 'none';

export interface TabWorkspaceResolution {
  accountId: string | null;
  source: TabWorkspaceSource;
}

function mintTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the arithmetic id */
  }
  return `tab_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Is this boot a RELOAD of the same tab (as opposed to a fresh open or a
 * "Duplicate tab")? Mirrors streamingFlag.ts's own check — kept local rather
 * than imported so this stays a dependency-free leaf.
 */
function isReloadNavigation(): boolean {
  try {
    const entries = performance.getEntriesByType('navigation') as Array<{ type?: string }>;
    return entries?.[0]?.type === 'reload';
  } catch {
    return false;
  }
}

function parsePin(raw: string | null): TabWorkspacePin | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TabWorkspacePin>;
    const accountId = typeof parsed.accountId === 'string' ? parsed.accountId.trim() : '';
    const tabId = typeof parsed.tabId === 'string' ? parsed.tabId.trim() : '';
    const uid = typeof parsed.uid === 'string' ? parsed.uid.trim() : '';
    if (!accountId) return null;
    const atMs = typeof parsed.atMs === 'number' && Number.isFinite(parsed.atMs) ? parsed.atMs : 0;
    return { tabId, uid, accountId, atMs };
  } catch {
    return null;
  }
}

/**
 * What a boot should do with whatever sessionStorage carries. Pure — the
 * decision is unit-tested without a browser.
 *
 * The hazard this table exists for: "Duplicate tab" copies sessionStorage
 * VERBATIM (streamingFlag.ts:52 documents the same trap for its lease keys),
 * so a duplicated tab arrives holding another live tab's identity. Inheriting
 * the ORG is right — you duplicated the tab to keep working where you were —
 * but the tab IDENTITY must be fresh, or two live tabs share one id.
 */
export function decideBootPin(input: {
  stored: TabWorkspacePin | null;
  isReload: boolean;
}): { action: 'keep' | 'inherit' | 'fresh'; accountId: string | null } {
  if (!input.stored) return { action: 'fresh', accountId: null };
  if (input.isReload) return { action: 'keep', accountId: input.stored.accountId };
  return { action: 'inherit', accountId: input.stored.accountId };
}

let bootedPin: TabWorkspacePin | null = null;
let booted = false;

/**
 * Read (and, on the first call of a page load, settle) this tab's pin.
 * Idempotent: the boot decision runs once per loaded page.
 */
export function readTabWorkspacePin(): TabWorkspacePin | null {
  if (booted) return bootedPin;
  booted = true;
  if (typeof sessionStorage === 'undefined') return null;
  let stored: TabWorkspacePin | null = null;
  try {
    stored = parsePin(sessionStorage.getItem(TAB_PIN_SESSION_KEY));
  } catch {
    return null;
  }
  const verdict = decideBootPin({ stored, isReload: isReloadNavigation() });
  if (!verdict.accountId) {
    bootedPin = null;
    return null;
  }
  // 'keep' is the same tab reloading — its identity survives with it.
  // 'inherit' is a duplicated tab: same org, brand-new identity.
  const tabId = verdict.action === 'keep' && stored?.tabId ? stored.tabId : mintTabId();
  bootedPin = {
    tabId,
    uid: stored?.uid ?? '',
    accountId: verdict.accountId,
    atMs: stored?.atMs ?? Date.now(),
  };
  persist(bootedPin);
  return bootedPin;
}

function persist(pin: TabWorkspacePin): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(TAB_PIN_SESSION_KEY, JSON.stringify(pin));
  } catch {
    /* private mode / quota — the tab falls back to the device + account tiers */
  }
}

/**
 * Pin this tab to a workspace. Called wherever a tab settles on an org: the
 * sealed switch's arrival markers, and the migration gate once it resolves.
 */
export function writeTabWorkspacePin(
  accountId: string | null | undefined,
  uid?: string | null,
): void {
  const clean = String(accountId ?? '').trim();
  if (!clean) return;
  const existing = readTabWorkspacePin();
  const pin: TabWorkspacePin = {
    tabId: existing?.tabId || mintTabId(),
    uid: String(uid ?? existing?.uid ?? '').trim(),
    accountId: clean,
    atMs: Date.now(),
  };
  bootedPin = pin;
  booted = true;
  persist(pin);
}

/** Drop this tab's pin (sign-out, or an authoritative membership revocation). */
export function clearTabWorkspacePin(): void {
  bootedPin = null;
  booted = true;
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(TAB_PIN_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * THE LADDER. `accountFromPointer` is the server back-pointer as this page
 * already resolved it (tier 3) — passed in rather than read here so this
 * module stays firebase-free.
 *
 * Fail-open by construction: with no pin and no device record this returns
 * exactly what the pointer said, which is today's behaviour byte for byte.
 */
export function resolveTabWorkspace(input: {
  uid: string | null | undefined;
  accountFromPointer: string | null | undefined;
  /** Membership filter — a pin naming an org the user no longer belongs to must never win. */
  isMember?: (accountId: string) => boolean;
  nowMs?: number;
}): TabWorkspaceResolution {
  const now = typeof input.nowMs === 'number' ? input.nowMs : Date.now();
  const allowed = (accountId: string): boolean => {
    if (!accountId) return false;
    return input.isMember ? input.isMember(accountId) === true : true;
  };

  const uid = String(input.uid ?? '').trim();
  const pin = readTabWorkspacePin();
  // A pin stamped by a DIFFERENT signed-in user is not this user's choice.
  // An unstamped pin (written before this field existed) is tolerated once and
  // re-stamped by the caller on the next settle.
  const pinIsOurs = pin ? !pin.uid || !uid || pin.uid === uid : false;
  if (pin && pinIsOurs && allowed(pin.accountId)) {
    return { accountId: pin.accountId, source: 'tab-pin' };
  }

  const device = readConfirmedWorkspace(input.uid);
  if (device && now - device.atMs <= DEVICE_SEED_MAX_AGE_MS && allowed(device.accountId)) {
    return { accountId: device.accountId, source: 'device' };
  }

  const pointer = String(input.accountFromPointer ?? '').trim();
  if (pointer) return { accountId: pointer, source: 'account' };

  return { accountId: null, source: 'none' };
}

/** Test seam: forget this page-load's boot decision (jsdom suites). */
export function __resetTabWorkspaceForTests(): void {
  bootedPin = null;
  booted = false;
}
